import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Phone, Mail } from 'lucide-react';

const cardData = {
  david: {
    name: 'David Raymond',
    phone: '(702) 796-5422',
    cell: '(702) 278-3313',
    fax: '(702) 796-4337',
    email: 'david@applevar.com',
    website: 'www.applevar.com',
    address: '1575 Cattle Ranch Place',
    city: 'Henderson, NV 89002',
    phoneRaw: '7027965422',
    cellRaw: '7022783313'
  },
  alfred: {
    name: 'Alfred Arnhold',
    phone: '(702) 796-5422',
    cell: '(702) 273-8007',
    fax: '(702) 796-4337',
    email: 'alfred@applevar.com',
    website: 'www.applevar.com',
    address: '1575 Cattle Ranch Place',
    city: 'Henderson, NV 89002',
    phoneRaw: '7027965422',
    cellRaw: '7022738007'
  }
};

const BusinessCard = ({ person, isAnimating }) => {
  const card = cardData[person];
  
  return (
    <div 
      className={`business-card ${isAnimating ? 'card-animate' : ''}`} 
      data-testid="business-card"
    >
      {/* Left side - Logo and Name */}
      <div className="card-left">
        {/* Gold foil logo with shimmer effect */}
        <div className={`logo-container ${isAnimating ? 'foil-animate' : ''}`}>
          <img 
            src="/assets/NPM_LOGO_Foil.png" 
            alt="NPM Computer Sales & Service"
            className="logo-foil logo-foil-base"
          />
          <img 
            src="/assets/NPM_LOGO_Foil_2.png" 
            alt=""
            className="logo-foil logo-foil-bright"
            aria-hidden="true"
          />
        </div>
        <div className="card-name">{card.name}</div>
      </div>
      
      {/* Right side - Contact Info */}
      <div className="card-right">
        <div className="card-address">
          <a 
            href={`https://maps.google.com/?q=${encodeURIComponent(card.address + ', ' + card.city)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {card.address}<br/>{card.city}
          </a>
        </div>
        
        <div className="card-phones">
          <div className="card-phone-row">
            <span className="phone-label">o</span>
            <a href={`tel:${card.phoneRaw}`}>{card.phone}</a>
          </div>
          <div className="card-phone-row">
            <span className="phone-label">c</span>
            <a href={`tel:${card.cellRaw}`}>{card.cell}</a>
          </div>
          <div className="card-phone-row">
            <span className="phone-label">f</span>
            <span>{card.fax}</span>
          </div>
        </div>
        
        <div className="card-links">
          <a href={`mailto:${card.email}`} className="card-email">{card.email}</a>
          <a href={`https://${card.website}`} target="_blank" rel="noopener noreferrer" className="card-website">{card.website}</a>
        </div>
      </div>
    </div>
  );
};

const ConnectPage = () => {
  const [selectedPerson, setSelectedPerson] = useState('david');
  const [isAnimating, setIsAnimating] = useState(false);
  const isFirstRender = useRef(true);
  
  // Handle toggle with animation
  const handleToggle = (person) => {
    if (person === selectedPerson) return;
    
    setIsAnimating(true);
    setSelectedPerson(person);
    
    // Reset animation state after animation completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 600);
  };
  
  // Skip animation on first render
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, []);
  
  return (
    <div className="relative overflow-hidden" data-testid="connect-page">
      {/* Hero Section */}
      <section className="relative hero-gradient section-padding-hero" data-testid="connect-hero">
        <div className="abstract-shape abstract-shape-1"></div>
        
        <div className="site-container relative">
          <div className="max-w-3xl prose-width">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3f3f3f] leading-[1.15] mb-5">
              Connect With Us
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
              Reach out for support, procurement, managed IT, security improvements, Microsoft 365 administration, network upgrades, voice systems, or automation projects.
            </p>
          </div>
        </div>
      </section>

      {/* Business Card Section */}
      <section className="py-16 md:py-20 bg-[#f8f8f6]" data-testid="business-card-section">
        <div className="site-container">
          <div className="flex flex-col items-center">
            {/* Toggle */}
            <div className="mb-8">
              <div className="inline-flex bg-white rounded-full p-1 shadow-sm border border-gray-200" data-testid="person-toggle">
                <button
                  onClick={() => handleToggle('david')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedPerson === 'david'
                      ? 'bg-[#7dc242] text-white'
                      : 'text-gray-500 hover:text-[#7dc242]'
                  }`}
                  data-testid="toggle-david"
                >
                  David Raymond
                </button>
                <button
                  onClick={() => handleToggle('alfred')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedPerson === 'alfred'
                      ? 'bg-[#7D77B7] text-white'
                      : 'text-gray-500 hover:text-[#7D77B7]'
                  }`}
                  data-testid="toggle-alfred"
                >
                  Alfred Arnhold
                </button>
              </div>
            </div>
            
            {/* Business Card */}
            <BusinessCard person={selectedPerson} isAnimating={isAnimating} />
            
            {/* Hours - minimal secondary info */}
            <div className="mt-10 text-center text-sm text-gray-500">
              <p>Monday – Friday, 8:00 AM – 5:00 PM · By appointment</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding cta-section" data-testid="connect-cta">
        <div className="site-container text-center">
          <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
            Schedule an Assessment Today
          </h2>
          <p className="text-base lg:text-lg text-gray-500 mb-6 max-w-2xl mx-auto">
            Let's discuss your IT needs and how NPM can help your business thrive.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg" 
              asChild 
              className="bg-[#7dc242] hover:bg-[#6ba83a] text-white border-0 h-11 px-6 text-[0.9375rem]"
              data-testid="final-cta-phone"
            >
              <a href="tel:+17027965422">
                <Phone className="w-[18px] h-[18px] mr-2" />
                Call (702) 796-5422
              </a>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              asChild 
              className="border-2 border-gray-300 hover:border-[#7dc242] hover:text-[#6ba83a] h-11 px-6 text-[0.9375rem]"
              data-testid="final-cta-email"
            >
              <a href="mailto:david@applevar.com">
                <Mail className="w-[18px] h-[18px] mr-2" />
                david@applevar.com
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConnectPage;
